import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type {
  MediaAssetDescriptor,
  JokerDefinition,
  QuizBoardCell,
  QuizBoardQuiz,
  QuizQuestion,
  QuizSet,
  RankDefinition,
} from "@quizshow/protocol";
import { createRoom, uploadRoomMedia } from "../api";
import { Brand } from "../components/brand";
import { LocalMediaPreview } from "../components/media";
import { ErrorBanner } from "../components/status";
import { exportQuizFile, importQuizFile } from "../import-export";
import { prepareMediaFile, roomMediaLimitBytes } from "../media";
import { normalizeSelectionMode, setCorrectAnswer } from "../question-editor-model";
import { storeCreatedRoom } from "../session";
import { deleteMedia, loadMedia, loadQuiz, saveMedia, saveQuiz } from "../storage/quiz-store";

const moduleLabels = {
  classic: "Classic Quiz Editor",
  "weakest-link": "Der Dümmste fliegt raus Editor",
  quizboard: "Quizboard Editor",
} as const;

type QuestionMediaKind = "image" | "audio" | "background";

function newQuestion(points = 100): QuizQuestion {
  return {
    questionId: crypto.randomUUID(),
    prompt: "Neue Frage",
    answers: [
      { optionId: crypto.randomUUID(), text: "Richtige Antwort", isCorrect: true },
      { optionId: crypto.randomUUID(), text: "Falsche Antwort", isCorrect: false },
    ],
    timerEnabled: true,
    timerSeconds: 300,
    points,
    fiftyFiftyRemoveCount: 1,
    questionType: "normal",
    answerMode: "multiple-choice",
    selectionMode: "single",
    evaluationMode: "manual",
    media: {},
  };
}

function allQuestions(quiz: QuizSet): QuizQuestion[] {
  return quiz.moduleId === "quizboard"
    ? quiz.categories.flatMap((category) => category.cells.map((cell) => cell.question))
    : quiz.questions;
}

function isQuizValid(quiz: QuizSet): boolean {
  const questions = allQuestions(quiz);
  return Boolean(
    quiz.title.trim() &&
      (quiz.scoringMode !== "rank-progress" || quiz.ranks.length > 0) &&
      questions.length &&
      (quiz.moduleId !== "quizboard" || quiz.categories.every((category) => category.name.trim() && category.cells.length)) &&
      questions.every(
        (question) =>
          question.prompt.trim() &&
          question.answers.length >= (question.answerMode === "free-text" ? 1 : 2) &&
          question.answers.every((answer) => answer.text.trim()) &&
          question.answers.some((answer) => answer.isCorrect) &&
          (question.answerMode !== "multiple-choice" ||
            question.selectionMode !== "single" ||
            question.answers.filter((answer) => answer.isCorrect).length === 1) &&
          (quiz.usesLives || question.bonusReward?.type !== "life") &&
          (question.questionType !== "bonus" ||
            (question.bonusReward &&
              (question.bonusReward.type !== "joker" || Boolean(question.bonusReward.jokerId)))),
      ),
  );
}

interface QuestionEditorProps {
  question: QuizQuestion;
  title: string;
  jokers: JokerDefinition[];
  usesLives: boolean;
  onChange: (question: QuizQuestion) => void;
  onAttach: (kind: QuestionMediaKind, file: File) => void;
  onRemove?: () => void;
  removeDisabled?: boolean;
}

function QuestionEditor({
  question,
  title,
  jokers,
  usesLives,
  onChange,
  onAttach,
  onRemove,
  removeDisabled,
}: QuestionEditorProps) {
  const updateAnswer = (optionId: string, updater: (answer: QuizQuestion["answers"][number]) => QuizQuestion["answers"][number]) => {
    onChange({
      ...question,
      answers: question.answers.map((answer) => (answer.optionId === optionId ? updater(answer) : answer)),
    });
  };
  const falseAnswers = question.answers.filter((answer) => !answer.isCorrect).length;

  return (
    <article className="card question-editor">
      <div className="section__heading">
        <h2>{title}</h2>
        {onRemove ? (
          <button className="button button--ghost button--small" disabled={removeDisabled} onClick={onRemove}>
            Entfernen
          </button>
        ) : null}
      </div>
      <label>
        Frage
        <textarea value={question.prompt} onChange={(event) => onChange({ ...question, prompt: event.target.value })} />
      </label>
      <div className="settings-grid">
        <label>
          Antworttyp
          <select
            value={question.answerMode}
            onChange={(event) => {
              const answerMode = event.target.value as QuizQuestion["answerMode"];
              onChange({
                ...question,
                answerMode,
                selectionMode: answerMode === "free-text" ? "single" : question.selectionMode,
                evaluationMode: answerMode === "free-text" ? "manual" : question.evaluationMode,
              });
            }}
          >
            <option value="multiple-choice">Feste Antworten</option>
            <option value="free-text">Freitext</option>
          </select>
        </label>
        {question.answerMode === "multiple-choice" ? (
          <label>
            Auswahlmodus
            <select
              value={question.selectionMode}
              onChange={(event) => {
                const selectionMode = event.target.value as QuizQuestion["selectionMode"];
                onChange({
                  ...question,
                  selectionMode,
                  answers: normalizeSelectionMode(question.answers, selectionMode),
                });
              }}
            >
              <option value="single">Single Choice – eine richtige Antwort</option>
              <option value="multiple">Multiple Choice – mehrere richtige Antworten</option>
            </select>
          </label>
        ) : null}
        <label>
          Bewertung
          <select
            value={question.evaluationMode}
            disabled={question.answerMode === "free-text"}
            onChange={(event) =>
              onChange({
                ...question,
                evaluationMode: event.target.value as QuizQuestion["evaluationMode"],
              })
            }
          >
            <option value="manual">Host bewertet manuell</option>
            <option value="automatic">Automatisch bewerten</option>
          </select>
        </label>
        <label>
          Fragetyp
          <select
            value={question.questionType ?? "normal"}
            onChange={(event) => {
              const questionType = event.target.value as QuizQuestion["questionType"];
              onChange({
                ...question,
                questionType,
                bonusReward:
                  questionType === "bonus"
                    ? question.bonusReward ?? { type: "points", amount: question.points || 100 }
                    : undefined,
              });
            }}
          >
            <option value="normal">Normale Frage</option>
            <option value="bonus">Bonusfrage</option>
          </select>
        </label>
        {question.questionType === "bonus" ? (
          <>
            <label>
              Bonusbelohnung
              <select
                value={question.bonusReward?.type ?? "points"}
                onChange={(event) =>
                  onChange({
                    ...question,
                    bonusReward: {
                      type: event.target.value as "points" | "life" | "joker",
                      amount: question.bonusReward?.amount ?? 1,
                      jokerId: event.target.value === "joker" ? jokers[0]?.jokerId : undefined,
                    },
                  })
                }
              >
                <option value="points">Punkte</option>
                {usesLives ? <option value="life">Leben</option> : null}
                <option value="joker">Joker</option>
              </select>
            </label>
            <label>
              Anzahl
              <input
                type="number"
                min={1}
                value={question.bonusReward?.amount ?? 1}
                onChange={(event) =>
                  onChange({
                    ...question,
                    bonusReward: {
                      type: question.bonusReward?.type ?? "points",
                      amount: Math.max(1, Number(event.target.value) || 1),
                      jokerId: question.bonusReward?.jokerId,
                    },
                  })
                }
              />
            </label>
            {question.bonusReward?.type === "joker" ? (
              <label>
                Joker
                <select
                  value={question.bonusReward.jokerId ?? ""}
                  onChange={(event) =>
                    onChange({
                      ...question,
                      bonusReward: { ...question.bonusReward!, jokerId: event.target.value },
                    })
                  }
                >
                  <option value="">Joker wählen</option>
                  {jokers.map((joker) => (
                    <option key={joker.jokerId} value={joker.jokerId}>
                      {joker.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </>
        ) : null}
      </div>
      {question.answerMode === "free-text" ? (
        <p className="muted">
          Die richtige Antwort und ihre Aliase dienen nur als Host-Hinweis. Freitext wird immer final durch Host oder
          Co-Host bewertet.
        </p>
      ) : null}
      <div className="answer-editor-list">
        {question.answers.map((answer, answerIndex) => (
          <div className="answer-editor answer-editor--aliases" key={answer.optionId}>
            <input
              aria-label={`Antwort ${answerIndex + 1} ist richtig`}
              type={
                question.answerMode === "multiple-choice" && question.selectionMode === "single"
                  ? "radio"
                  : "checkbox"
              }
              name={`correct-${question.questionId}`}
              checked={answer.isCorrect}
              onChange={(event) => {
                if (question.answerMode === "multiple-choice" && question.selectionMode === "single") {
                  onChange({
                    ...question,
                    answers: setCorrectAnswer(
                      question.answers,
                      answer.optionId,
                      question.selectionMode,
                      event.target.checked,
                    ),
                  });
                  return;
                }
                onChange({
                  ...question,
                  answers: setCorrectAnswer(
                    question.answers,
                    answer.optionId,
                    question.selectionMode,
                    event.target.checked,
                  ),
                });
              }}
            />
            <input
              value={answer.text}
              aria-label={`Antwort ${answerIndex + 1}`}
              onChange={(event) => updateAnswer(answer.optionId, (entry) => ({ ...entry, text: event.target.value }))}
            />
            <input
              value={answer.aliases?.join(", ") ?? ""}
              aria-label={`Aliase für Antwort ${answerIndex + 1}`}
              placeholder="Aliase, mit Komma"
              onChange={(event) =>
                updateAnswer(answer.optionId, (entry) => ({
                  ...entry,
                  aliases: event.target.value
                    .split(",")
                    .map((alias) => alias.trim())
                    .filter(Boolean),
                }))
              }
            />
            <button
              className="button button--ghost button--small"
              disabled={question.answers.length <= (question.answerMode === "free-text" ? 1 : 2)}
              onClick={() =>
                onChange({ ...question, answers: question.answers.filter((candidate) => candidate.optionId !== answer.optionId) })
              }
            >
              Entfernen
            </button>
          </div>
        ))}
      </div>
      <button
        className="button button--ghost"
        disabled={question.answers.length >= 20}
        onClick={() =>
          onChange({
            ...question,
            answers: [
              ...question.answers,
              { optionId: crypto.randomUUID(), text: `Antwort ${question.answers.length + 1}`, isCorrect: false },
            ],
          })
        }
      >
        {question.answerMode === "free-text" ? "Weitere Musterantwort hinzufügen" : "Antwort hinzufügen"}
      </button>
      <div className="settings-grid">
        <label className="checkbox">
          <input
            type="checkbox"
            checked={question.timerEnabled}
            onChange={(event) =>
              onChange({ ...question, timerEnabled: event.target.checked, timerSeconds: question.timerSeconds || 300 })
            }
          />
          Fragentimer
        </label>
        <label>
          Sekunden
          <input
            type="number"
            min={5}
            max={3600}
            value={question.timerSeconds}
            onChange={(event) => onChange({ ...question, timerSeconds: Number(event.target.value) || 300 })}
          />
        </label>
        <label>
          Punkte
          <input
            type="number"
            min={0}
            value={question.points}
            onChange={(event) => onChange({ ...question, points: Number(event.target.value) })}
          />
        </label>
        <label>
          50:50 entfernt
          <input
            type="number"
            min={1}
            max={Math.max(1, falseAnswers)}
            value={question.fiftyFiftyRemoveCount}
            onChange={(event) =>
              onChange({
                ...question,
                fiftyFiftyRemoveCount: Math.min(Math.max(1, Number(event.target.value)), Math.max(1, falseAnswers)),
              })
            }
          />
        </label>
      </div>
      <details>
        <summary>Medien, Darstellung und Admin-Notizen</summary>
        <div className="media-input-grid">
          <label>
            Fragebild (max. 5 MB)
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(event) => event.target.files?.[0] && onAttach("image", event.target.files[0])}
            />
            <LocalMediaPreview mediaId={question.media.imageMediaId} kind="image" />
          </label>
          <label>
            Hintergrund (max. 5 MB)
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(event) => event.target.files?.[0] && onAttach("background", event.target.files[0])}
            />
            <LocalMediaPreview mediaId={question.media.backgroundMediaId} kind="image" />
          </label>
          <label>
            Audio (max. 15 MB)
            <input
              type="file"
              accept="audio/mpeg,audio/ogg,audio/wav"
              onChange={(event) => event.target.files?.[0] && onAttach("audio", event.target.files[0])}
            />
            <LocalMediaPreview mediaId={question.media.audioMediaId} kind="audio" />
          </label>
          <label>
            Bildgröße
            <select
              value={question.media.imageSize ?? "normal"}
              onChange={(event) =>
                onChange({
                  ...question,
                  media: { ...question.media, imageSize: event.target.value as "normal" | "large" | "fullscreen" },
                })
              }
            >
              <option value="normal">Normal</option>
              <option value="large">Groß</option>
              <option value="fullscreen">Vollbild</option>
            </select>
          </label>
          <label>
            Hintergrundfarbe
            <input
              type="color"
              value={question.media.backgroundColor ?? "#100b11"}
              onChange={(event) =>
                onChange({ ...question, media: { ...question.media, backgroundColor: event.target.value } })
              }
            />
          </label>
        </div>
        <label>
          Admin-Notizen
          <textarea
            value={question.adminNotes ?? ""}
            onChange={(event) => onChange({ ...question, adminNotes: event.target.value })}
          />
        </label>
      </details>
      <details className="question-preview">
        <summary>Frage-Vorschau</summary>
        <span className="eyebrow">{question.points} Punkte</span>
        <h3>{question.prompt || "Fragetext"}</h3>
        <div className="answer-grid">
          {question.answers.map((answer) => (
            <div className={`answer-card ${answer.isCorrect ? "answer-card--correct" : ""}`} key={answer.optionId}>
              {answer.text || "Antwort"}
            </div>
          ))}
        </div>
      </details>
    </article>
  );
}

export function EditorPage() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState<QuizSet>();
  const [saveState, setSaveState] = useState("Lädt...");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!quizId) return;
    void loadQuiz(quizId).then((loaded) => {
      if (!loaded) {
        setError("Quiz wurde nicht gefunden.");
        return;
      }
      setQuiz(loaded);
      setSaveState("Gespeichert");
    });
  }, [quizId]);

  useEffect(() => {
    if (!quiz) return;
    setSaveState("Speichert...");
    const id = window.setTimeout(() => {
      void saveQuiz({ ...quiz, updatedAt: new Date().toISOString() } as QuizSet)
        .then(() => setSaveState("Automatisch gespeichert"))
        .catch(() => setSaveState("Speichern fehlgeschlagen"));
    }, 600);
    return () => window.clearTimeout(id);
  }, [quiz]);

  const valid = useMemo(() => (quiz ? isQuizValid(quiz) : false), [quiz]);

  if (!quiz) {
    return (
      <main className="shell">
        <Brand />
        <ErrorBanner message={error ?? "Quiz wird geladen..."} />
      </main>
    );
  }

  const mediaField = {
    image: "imageMediaId",
    audio: "audioMediaId",
    background: "backgroundMediaId",
  } as const;

  const attachMedia = async (
    question: QuizQuestion,
    kind: QuestionMediaKind,
    file: File,
  ): Promise<{ question: QuizQuestion; assets: MediaAssetDescriptor[] }> => {
    const prepared = await prepareMediaFile(file, kind);
    const oldId = question.media[mediaField[kind]];
    const retained = quiz.mediaAssets.filter((asset) => asset.mediaId !== oldId);
    const totalBytes = retained.reduce((total, asset) => total + asset.bytes, 0) + prepared.descriptor.bytes;
    if (totalBytes > roomMediaLimitBytes) throw new Error("Das Medienlimit von 100 MB pro Quiz ist erreicht.");
    await saveMedia(prepared.descriptor.mediaId, prepared.blob);
    if (oldId) await deleteMedia(oldId);
    return {
      question: {
        ...question,
        media: { ...question.media, [mediaField[kind]]: prepared.descriptor.mediaId },
      },
      assets: [...retained, prepared.descriptor],
    };
  };

  const updateSequentialQuestion = (questionId: string, updated: QuizQuestion) => {
    if (quiz.moduleId === "quizboard") return;
    setQuiz({ ...quiz, questions: quiz.questions.map((question) => (question.questionId === questionId ? updated : question)) });
  };

  const attachSequentialMedia = async (question: QuizQuestion, kind: QuestionMediaKind, file: File) => {
    try {
      const attached = await attachMedia(question, kind, file);
      if (quiz.moduleId === "quizboard") return;
      setQuiz({
        ...quiz,
        mediaAssets: attached.assets,
        questions: quiz.questions.map((entry) => (entry.questionId === question.questionId ? attached.question : entry)),
      });
    } catch (mediaError) {
      setError(mediaError instanceof Error ? mediaError.message : "Medium konnte nicht verarbeitet werden.");
    }
  };

  const updateBoardCell = (categoryId: string, cellId: string, updater: (cell: QuizBoardCell) => QuizBoardCell) => {
    if (quiz.moduleId !== "quizboard") return;
    setQuiz({
      ...quiz,
      categories: quiz.categories.map((category) =>
        category.categoryId === categoryId
          ? { ...category, cells: category.cells.map((cell) => (cell.cellId === cellId ? updater(cell) : cell)) }
          : category,
      ),
    });
  };

  const attachBoardMedia = async (
    categoryId: string,
    cell: QuizBoardCell,
    kind: QuestionMediaKind,
    file: File,
  ) => {
    try {
      const attached = await attachMedia(cell.question, kind, file);
      if (quiz.moduleId !== "quizboard") return;
      setQuiz({
        ...quiz,
        mediaAssets: attached.assets,
        categories: quiz.categories.map((category) =>
          category.categoryId === categoryId
            ? {
                ...category,
                cells: category.cells.map((entry) =>
                  entry.cellId === cell.cellId ? { ...entry, question: attached.question } : entry,
                ),
              }
            : category,
        ),
      });
    } catch (mediaError) {
      setError(mediaError instanceof Error ? mediaError.message : "Medium konnte nicht verarbeitet werden.");
    }
  };

  const startRoom = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const finalQuiz = { ...quiz, updatedAt: new Date().toISOString() } as QuizSet;
      await saveQuiz(finalQuiz);
      const response = await createRoom(finalQuiz);
      for (const asset of finalQuiz.mediaAssets) {
        const blob = await loadMedia(asset.mediaId);
        if (!blob) throw new Error(`Medium "${asset.fileName}" fehlt lokal.`);
        await uploadRoomMedia(response.roomId, response.hostToken, asset.mediaId, blob);
      }
      storeCreatedRoom(response);
      navigate(`/host/${response.roomId}`);
    } catch (roomError) {
      setError(roomError instanceof Error ? roomError.message : "Raum konnte nicht erstellt werden.");
    } finally {
      setBusy(false);
    }
  };

  const updateQuizMeta = (updates: Partial<QuizSet>) => setQuiz({ ...quiz, ...updates } as QuizSet);

  const importFile = async (file: File | undefined) => {
    if (!file) return;
    setError(undefined);
    try {
      const imported = await importQuizFile(file);
      navigate(`/editor/${imported.quizId}`);
    } catch (importError) {
      setError(importError instanceof Error ? importError.message : "Import fehlgeschlagen.");
    } finally {
      if (importRef.current) importRef.current.value = "";
    }
  };

  const updateRank = (rankId: string, updater: (rank: RankDefinition) => RankDefinition) => {
    setQuiz({ ...quiz, ranks: quiz.ranks.map((rank) => (rank.rankId === rankId ? updater(rank) : rank)) });
  };

  const attachRankImage = async (rank: RankDefinition, file: File) => {
    try {
      const prepared = await prepareMediaFile(file, "icon");
      const retained = quiz.mediaAssets.filter((asset) => asset.mediaId !== rank.imageMediaId);
      const totalBytes = retained.reduce((total, asset) => total + asset.bytes, 0) + prepared.descriptor.bytes;
      if (totalBytes > roomMediaLimitBytes) throw new Error("Das Medienlimit von 100 MB pro Quiz ist erreicht.");
      await saveMedia(prepared.descriptor.mediaId, prepared.blob);
      if (rank.imageMediaId) await deleteMedia(rank.imageMediaId);
      setQuiz({
        ...quiz,
        mediaAssets: [...retained, prepared.descriptor],
        ranks: quiz.ranks.map((entry) =>
          entry.rankId === rank.rankId ? { ...entry, imageMediaId: prepared.descriptor.mediaId } : entry,
        ),
      });
    } catch (mediaError) {
      setError(mediaError instanceof Error ? mediaError.message : "Rangbild konnte nicht verarbeitet werden.");
    }
  };

  const attachJokerImage = async (joker: JokerDefinition, file: File) => {
    try {
      const prepared = await prepareMediaFile(file, "icon");
      const retained = quiz.mediaAssets.filter((asset) => asset.mediaId !== joker.imageMediaId);
      const totalBytes = retained.reduce((total, asset) => total + asset.bytes, 0) + prepared.descriptor.bytes;
      if (totalBytes > roomMediaLimitBytes) throw new Error("Das Medienlimit von 100 MB pro Quiz ist erreicht.");
      await saveMedia(prepared.descriptor.mediaId, prepared.blob);
      if (joker.imageMediaId) await deleteMedia(joker.imageMediaId);
      setQuiz({
        ...quiz,
        mediaAssets: [...retained, prepared.descriptor],
        jokers: quiz.jokers.map((entry) =>
          entry.jokerId === joker.jokerId ? { ...entry, imageMediaId: prepared.descriptor.mediaId } : entry,
        ),
      });
    } catch (mediaError) {
      setError(mediaError instanceof Error ? mediaError.message : "Jokerbild konnte nicht verarbeitet werden.");
    }
  };

  return (
    <main className="shell">
      <Brand />
      <ErrorBanner message={error} onClose={() => setError(undefined)} />
      <div className="editor-toolbar">
        <Link className="button button--ghost" to="/">
          Zur Bibliothek
        </Link>
        <button className="button button--ghost" onClick={() => void exportQuizFile(quiz).catch((value) => setError(String(value)))}>
          Exportieren
        </button>
        <button className="button button--ghost" onClick={() => importRef.current?.click()}>
          Importieren
        </button>
        <input
          ref={importRef}
          className="visually-hidden"
          type="file"
          accept=".qss,.json,application/json"
          onChange={(event) => void importFile(event.target.files?.[0])}
        />
        <span className="save-state">{saveState}</span>
        <button className="button button--accent" disabled={!valid || busy} onClick={() => void startRoom()}>
          {busy ? "Medien und Raum werden vorbereitet..." : "Online-Raum starten"}
        </button>
      </div>

      <section className="card editor-meta">
        <span className="eyebrow">{moduleLabels[quiz.moduleId]}</span>
        <label>
          Quizname
          <input value={quiz.title} onChange={(event) => updateQuizMeta({ title: event.target.value })} maxLength={120} />
        </label>
        <label>
          Beschreibung
          <textarea
            value={quiz.description ?? ""}
            onChange={(event) => updateQuizMeta({ description: event.target.value })}
            maxLength={2000}
          />
        </label>
        <div className="theme-grid">
          <label>
            Hauptfarbe
            <input
              type="color"
              value={quiz.theme.primaryColor}
              onChange={(event) => setQuiz({ ...quiz, theme: { ...quiz.theme, primaryColor: event.target.value } })}
            />
          </label>
          <label>
            Akzent
            <input
              type="color"
              value={quiz.theme.accentColor}
              onChange={(event) => setQuiz({ ...quiz, theme: { ...quiz.theme, accentColor: event.target.value } })}
            />
          </label>
          <label>
            Hintergrund
            <input
              type="color"
              value={quiz.theme.backgroundColor}
              onChange={(event) => setQuiz({ ...quiz, theme: { ...quiz.theme, backgroundColor: event.target.value } })}
            />
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={quiz.allowSelfTeamSelection}
              onChange={(event) => updateQuizMeta({ allowSelfTeamSelection: event.target.checked })}
            />
            Freie Teamwahl in der Lobby
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={quiz.autoOpenBuzzer}
              onChange={(event) => updateQuizMeta({ autoOpenBuzzer: event.target.checked })}
            />
            Buzzer beim Fragenstart automatisch freigeben
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={quiz.allowParticipantJokers ?? false}
              onChange={(event) => updateQuizMeta({ allowParticipantJokers: event.target.checked })}
            />
            Teilnehmer dürfen Joker selbst einsetzen oder anfragen
          </label>
        </div>
        <span className="muted">
          Medien: {(quiz.mediaAssets.reduce((total, asset) => total + asset.bytes, 0) / 1024 / 1024).toFixed(1)} / 100 MB
        </span>
        {quiz.moduleId === "weakest-link" ? (
          <div className="settings-grid">
            <label>
              Startleben pro Spieler
              <input
                type="number"
                min={1}
                max={20}
                value={quiz.startingLives}
                onChange={(event) => setQuiz({ ...quiz, startingLives: Number(event.target.value) })}
              />
            </label>
            <label>
              Fragenreihenfolge
              <select
                value={quiz.questionOrder}
                onChange={(event) =>
                  setQuiz({ ...quiz, questionOrder: event.target.value as "fixed" | "random" })
                }
              >
                <option value="fixed">Fest</option>
                <option value="random">Zufällig pro Raum</option>
              </select>
            </label>
            <span className="muted">Leben sind in diesem Modus immer aktiv.</span>
          </div>
        ) : null}
        {quiz.moduleId !== "weakest-link" ? (
          <div className="settings-grid">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={quiz.usesLives}
                onChange={(event) => setQuiz({ ...quiz, usesLives: event.target.checked })}
              />
              Optionalen Lebensmodus verwenden
            </label>
            {quiz.usesLives ? (
              <label>
                Startleben
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={quiz.defaultLives}
                  onChange={(event) =>
                    setQuiz({ ...quiz, defaultLives: Math.max(1, Number(event.target.value) || 3) })
                  }
                />
              </label>
            ) : null}
          </div>
        ) : null}

        {quiz.moduleId === "classic" ? (
          <section className="rank-editor card">
            <div className="section__heading">
              <div>
                <span className="eyebrow">Classic-Modus</span>
                <h2>Ränge</h2>
              </div>
              <span className="muted">{quiz.ranks.length} definiert</span>
            </div>
            <p className="muted">
              Aktivieren Sie „Rangfortschritt statt Punkte“, damit richtige normale Fragen den Fortschritt erhöhen.
            </p>
            <label>
              Wertungsmodus
              <select
                value={quiz.scoringMode}
                onChange={(event) =>
                  setQuiz({ ...quiz, scoringMode: event.target.value as "points" | "rank-progress" })
                }
              >
                <option value="points">Punkte</option>
                <option value="rank-progress">Rangfortschritt</option>
              </select>
            </label>
            <div className="configuration-list">
              {quiz.ranks.map((rank) => (
                <div className="card configuration-row" key={rank.rankId}>
                  <input
                    aria-label="Rangname"
                    value={rank.name}
                    onChange={(event) => updateRank(rank.rankId, (entry) => ({ ...entry, name: event.target.value }))}
                  />
                  <input
                    aria-label="Rangsymbol"
                    value={rank.icon ?? ""}
                    maxLength={16}
                    placeholder="Icon"
                    onChange={(event) => updateRank(rank.rankId, (entry) => ({ ...entry, icon: event.target.value }))}
                  />
                  <label>
                    Text
                    <input
                      type="color"
                      value={rank.color}
                      onChange={(event) => updateRank(rank.rankId, (entry) => ({ ...entry, color: event.target.value }))}
                    />
                  </label>
                  <label>
                    Hintergrund
                    <input
                      type="color"
                      value={rank.backgroundColor}
                      onChange={(event) =>
                        updateRank(rank.rankId, (entry) => ({ ...entry, backgroundColor: event.target.value }))
                      }
                    />
                  </label>
                  <label>
                    Ab Fortschritt
                    <input
                      type="number"
                      min={0}
                      value={rank.requiredProgress}
                      onChange={(event) =>
                        updateRank(rank.rankId, (entry) => ({
                          ...entry,
                          requiredProgress: Math.max(0, Number(event.target.value) || 0),
                        }))
                      }
                    />
                  </label>
                  <label>
                    Rangbild
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(event) => event.target.files?.[0] && void attachRankImage(rank, event.target.files[0])}
                    />
                    <LocalMediaPreview mediaId={rank.imageMediaId} kind="image" />
                  </label>
                  <button
                    className="button button--ghost button--small"
                    disabled={quiz.scoringMode === "rank-progress" && quiz.ranks.length === 1}
                    onClick={() => setQuiz({ ...quiz, ranks: quiz.ranks.filter((entry) => entry.rankId !== rank.rankId) })}
                  >
                    Rang entfernen
                  </button>
                </div>
              ))}
            </div>
            <button
              className="button button--ghost"
              onClick={() =>
                setQuiz({
                  ...quiz,
                  ranks: [
                    ...quiz.ranks,
                    {
                      rankId: crypto.randomUUID(),
                      name: `Rang ${quiz.ranks.length + 1}`,
                      color: "#ffffff",
                      backgroundColor: quiz.theme.primaryColor,
                      icon: "",
                      requiredProgress: quiz.ranks.length,
                    },
                  ],
                })
              }
            >
              Rang hinzufügen
            </button>
          </section>
        ) : null}

        {quiz.moduleId !== "quizboard" ? (
          <details>
            <summary>Joker</summary>
            <div className="configuration-list">
              {quiz.jokers.map((joker) => (
                <div className="card configuration-row" key={joker.jokerId}>
                  <input
                    aria-label="Jokername"
                    value={joker.name}
                    onChange={(event) =>
                      setQuiz({
                        ...quiz,
                        jokers: quiz.jokers.map((entry) =>
                          entry.jokerId === joker.jokerId ? { ...entry, name: event.target.value } : entry,
                        ),
                      })
                    }
                  />
                  <input
                    aria-label="Jokerbeschreibung"
                    value={joker.description ?? ""}
                    placeholder="Beschreibung"
                    onChange={(event) =>
                      setQuiz({
                        ...quiz,
                        jokers: quiz.jokers.map((entry) =>
                          entry.jokerId === joker.jokerId ? { ...entry, description: event.target.value } : entry,
                        ),
                      })
                    }
                  />
                  <input
                    aria-label="Jokersymbol"
                    value={joker.icon ?? ""}
                    maxLength={16}
                    placeholder="Icon"
                    onChange={(event) =>
                      setQuiz({
                        ...quiz,
                        jokers: quiz.jokers.map((entry) =>
                          entry.jokerId === joker.jokerId ? { ...entry, icon: event.target.value } : entry,
                        ),
                      })
                    }
                  />
                  <select
                    aria-label="Jokertyp"
                    value={joker.type}
                    onChange={(event) =>
                      setQuiz({
                        ...quiz,
                        jokers: quiz.jokers.map((entry) =>
                          entry.jokerId === joker.jokerId
                            ? { ...entry, type: event.target.value as JokerDefinition["type"] }
                            : entry,
                        ),
                      })
                    }
                  >
                    <option value="fifty-fifty">50:50</option>
                    <option value="custom">Benutzerdefiniert</option>
                    <option value="pass-question">Frage weitergeben</option>
                  </select>
                  <label>
                    Jokerbild
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(event) =>
                        event.target.files?.[0] && void attachJokerImage(joker, event.target.files[0])
                      }
                    />
                    <LocalMediaPreview mediaId={joker.imageMediaId} kind="image" />
                  </label>
                  <label>
                    Farbe
                    <input
                      type="color"
                      value={joker.color}
                      onChange={(event) =>
                        setQuiz({
                          ...quiz,
                          jokers: quiz.jokers.map((entry) =>
                            entry.jokerId === joker.jokerId ? { ...entry, color: event.target.value } : entry,
                          ),
                        })
                      }
                    />
                  </label>
                  <label>
                    Startanzahl
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={joker.startingCount}
                      onChange={(event) =>
                        setQuiz({
                          ...quiz,
                          jokers: quiz.jokers.map((entry) =>
                            entry.jokerId === joker.jokerId
                              ? { ...entry, startingCount: Math.max(0, Number(event.target.value) || 0) }
                              : entry,
                          ),
                        })
                      }
                    />
                  </label>
                  <button
                    className="button button--ghost button--small"
                    onClick={() =>
                      setQuiz({ ...quiz, jokers: quiz.jokers.filter((entry) => entry.jokerId !== joker.jokerId) })
                    }
                  >
                    Joker entfernen
                  </button>
                </div>
              ))}
            </div>
            <button
              className="button button--ghost"
              onClick={() =>
                setQuiz({
                  ...quiz,
                  jokers: [
                    ...quiz.jokers,
                    {
                      jokerId: crypto.randomUUID(),
                      name: `Joker ${quiz.jokers.length + 1}`,
                      description: "",
                      color: quiz.theme.accentColor,
                      icon: "J",
                      type: "custom",
                      startingCount: 0,
                    },
                  ],
                })
              }
            >
              Joker hinzufügen
            </button>
          </details>
        ) : null}
      </section>

      {quiz.moduleId !== "quizboard" ? (
        <section className="question-list">
          {quiz.questions.map((question, questionIndex) => (
            <QuestionEditor
              key={question.questionId}
              question={question}
              title={`Frage ${questionIndex + 1}`}
              jokers={quiz.jokers}
              usesLives={quiz.usesLives}
              onChange={(updated) => updateSequentialQuestion(question.questionId, updated)}
              onAttach={(kind, file) => void attachSequentialMedia(question, kind, file)}
              removeDisabled={quiz.questions.length === 1}
              onRemove={() =>
                setQuiz({ ...quiz, questions: quiz.questions.filter((entry) => entry.questionId !== question.questionId) })
              }
            />
          ))}
          <button
            className="button button--primary"
            onClick={() => setQuiz({ ...quiz, questions: [...quiz.questions, newQuestion()] })}
          >
            Frage hinzufügen
          </button>
        </section>
      ) : (
        <QuizBoardEditor
          quiz={quiz}
          setQuiz={setQuiz}
          updateCell={updateBoardCell}
          attachMedia={(categoryId, cell, kind, file) => void attachBoardMedia(categoryId, cell, kind, file)}
        />
      )}
    </main>
  );
}

function QuizBoardEditor({
  quiz,
  setQuiz,
  updateCell,
  attachMedia,
}: {
  quiz: QuizBoardQuiz;
  setQuiz: (quiz: QuizBoardQuiz) => void;
  updateCell: (categoryId: string, cellId: string, updater: (cell: QuizBoardCell) => QuizBoardCell) => void;
  attachMedia: (
    categoryId: string,
    cell: QuizBoardCell,
    kind: QuestionMediaKind,
    file: File,
  ) => void;
}) {
  return (
    <section className="question-list board-editor">
      {quiz.categories.map((category, categoryIndex) => (
        <article className="card board-category-editor" key={category.categoryId}>
          <div className="section__heading">
            <label>
              Kategorie {categoryIndex + 1}
              <input
                value={category.name}
                onChange={(event) =>
                  setQuiz({
                    ...quiz,
                    categories: quiz.categories.map((entry) =>
                      entry.categoryId === category.categoryId ? { ...entry, name: event.target.value } : entry,
                    ),
                  })
                }
              />
            </label>
            <button
              className="button button--ghost button--small"
              disabled={quiz.categories.length === 1}
              onClick={() =>
                setQuiz({ ...quiz, categories: quiz.categories.filter((entry) => entry.categoryId !== category.categoryId) })
              }
            >
              Kategorie entfernen
            </button>
          </div>
          {category.cells.map((cell, cellIndex) => (
            <details className="board-cell-editor" key={cell.cellId}>
              <summary>
                Feld {cellIndex + 1}: {cell.points} Punkte
              </summary>
              <div className="settings-grid">
                <label>
                  Punkte
                  <input
                    type="number"
                    min={0}
                    value={cell.points}
                    onChange={(event) =>
                      updateCell(category.categoryId, cell.cellId, (entry) => ({
                        ...entry,
                        points: Number(event.target.value),
                        question: { ...entry.question, points: Number(event.target.value) },
                      }))
                    }
                  />
                </label>
                <label>
                  Spezialfeld
                  <select
                    value={cell.special}
                    onChange={(event) =>
                      updateCell(category.categoryId, cell.cellId, (entry) => ({
                        ...entry,
                        special: event.target.value as "none" | "double",
                      }))
                    }
                  >
                    <option value="none">Normal</option>
                    <option value="double">Doppelte Punkte</option>
                  </select>
                </label>
              </div>
              <QuestionEditor
                question={cell.question}
                title={`${category.name}: ${cell.points}`}
                jokers={quiz.jokers}
                usesLives={quiz.usesLives}
                onChange={(question) => updateCell(category.categoryId, cell.cellId, (entry) => ({ ...entry, question }))}
                onAttach={(kind, file) => attachMedia(category.categoryId, cell, kind, file)}
                onRemove={() =>
                  setQuiz({
                    ...quiz,
                    categories: quiz.categories.map((entry) =>
                      entry.categoryId === category.categoryId
                        ? { ...entry, cells: entry.cells.filter((candidate) => candidate.cellId !== cell.cellId) }
                        : entry,
                    ),
                  })
                }
                removeDisabled={category.cells.length === 1}
              />
            </details>
          ))}
          <button
            className="button button--primary"
            onClick={() => {
              const points = (category.cells.at(-1)?.points ?? 0) + 100;
              setQuiz({
                ...quiz,
                categories: [
                  ...quiz.categories.filter((entry) => entry.categoryId !== category.categoryId),
                  {
                    ...category,
                    cells: [
                      ...category.cells,
                      { cellId: crypto.randomUUID(), points, special: "none" as const, question: newQuestion(points) },
                    ],
                  },
                ].sort(
                  (left, right) =>
                    quiz.categories.findIndex((entry) => entry.categoryId === left.categoryId) -
                    quiz.categories.findIndex((entry) => entry.categoryId === right.categoryId),
                ),
              });
            }}
          >
            Punktefeld hinzufügen
          </button>
        </article>
      ))}
      <button
        className="button button--accent"
        onClick={() =>
          setQuiz({
            ...quiz,
            categories: [
              ...quiz.categories,
              {
                categoryId: crypto.randomUUID(),
                name: "Neue Kategorie",
                cells: [{ cellId: crypto.randomUUID(), points: 100, special: "none" as const, question: newQuestion(100) }],
              },
            ],
          })
        }
      >
        Kategorie hinzufügen
      </button>
    </section>
  );
}
